import React, { useCallback, useMemo, useState } from 'react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Box,
  EyeIcon,
  HideIcon,
  Inscription,
  RadioButton,
  RawButton,
  TextWithPointer,
  WarningCard,
  WarningIcon,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import Permissions from '@/common/permissions';
import regexp from '@/common/regexp';
import { useHasAccess } from '@/modules/core';
import { formatCurrency } from '@/utils';

import { CombineHSASectionType, HSAAccountStatus, HSACombineAccount } from './types/combineHSA.types';

const EMPTY_VALUE = '—';

const formatStatus = (status: HSAAccountStatus): string =>
  status
    .toLowerCase()
    .replace(regexp.DASH_SYMBOL, ' ')
    .replace(regexp.FIRST_LETTER, (c) => c.toUpperCase());

const INVESTMENT_WARNING_MESSAGE =
  'Investment portfolio associated to the account being closed must be liquidated prior to combining accounts.';
const ZERO_BALANCE_MESSAGE = 'This account has a $0 balance and is not eligible for combining.';
const PENDING_ACCOUNT_MESSAGE = 'Funds cannot be moved into this account until the account has passed CIP.';

const SSN_VIEW_PERMISSION = [{ permission: Permissions.SSN_VIEW }];

const CardWrapper = styled(Box)``;

const StatusDot = styled(Box)<{ status: HSAAccountStatus }>`
  width: 8px;
  height: 8px;
  min-width: 8px;
  border-radius: 999px;
  background: ${({ status, theme }) => {
    if (status === HSAAccountStatus.OPEN) return theme.colors.success;
    if (status === HSAAccountStatus.PENDING) return theme.colors.warning;
    return theme.colors.danger;
  }};
`;

interface FieldProps {
  label: string;
  value: React.ReactNode;
}

interface Props {
  account: HSACombineAccount;
  sectionType: CombineHSASectionType;
  isSelected?: boolean;
  isDisabledByOtherSection?: boolean;
  otherSectionAccount?: HSACombineAccount | null;
  onSelect?: (account: HSACombineAccount) => void;
  forceExpanded?: boolean;
  hideRadio?: boolean;
  defaultExpanded?: boolean;
}

const CardField: React.FC<FieldProps> = ({ label, value }) => (
  <Box flex>
    <Inscription size="small" color="textSecondary" lineHeight="16px">{label}</Inscription>
    <Box margin={{ top: 'spacing2' }}>{value}</Box>
  </Box>
);

export const CombineHSAEmployeeCard: React.FC<Props> = ({
  account,
  sectionType,
  isSelected,
  isDisabledByOtherSection,
  otherSectionAccount,
  onSelect,
  forceExpanded,
  hideRadio,
  defaultExpanded,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded ?? false);
  const [isSsnShown, setIsSsnShown] = useState(false);
  const hasSsnAccess = useHasAccess(SSN_VIEW_PERMISSION);

  const formattedSsn = useMemo(() => {
    if (!account.ssn) return EMPTY_VALUE;
    if (isSsnShown && hasSsnAccess) return account.ssn;
    return `*****${account.ssn.slice(-4)}`;
  }, [account.ssn, hasSsnAccess, isSsnShown]);

  const hasInvestmentWarning = useMemo(() => {
    if (!otherSectionAccount || otherSectionAccount.accountId === account.accountId) return false;
    const [sourceAccount, targetAccount] =
      sectionType === CombineHSASectionType.FROM ? [account, otherSectionAccount] : [otherSectionAccount, account];

    return (sourceAccount.investorAccountBalance || 0) > 0 && targetAccount.investorAccountBalance !== undefined;
  }, [sectionType, account, otherSectionAccount]);
  const hasZeroBalanceWarning = useMemo(
    () => sectionType === CombineHSASectionType.FROM && account.availableBalance === 0,
    [sectionType, account.availableBalance]
  );
  const hasPendingWarning = useMemo(
    () => sectionType === CombineHSASectionType.TO && account.accountStatus === HSAAccountStatus.PENDING,
    [sectionType, account.accountStatus]
  );
  const isRadioDisabled = useMemo(
    () => hasInvestmentWarning || hasZeroBalanceWarning || hasPendingWarning || isDisabledByOtherSection,
    [hasInvestmentWarning, hasZeroBalanceWarning, hasPendingWarning, isDisabledByOtherSection]
  );

  const handleToggle = useCallback(() => setIsExpanded((prev) => !prev), []);
  const handleSelect = useCallback(() => onSelect?.(account), [onSelect, account]);

  return (
    <CardWrapper
      data-testid={`combine-hsa-section_${sectionType}_card_${account.accountNumber}`}
      direction="column"
      gap="spacing12"
      overflow="hidden"
      border={{ size: 'small', color: 'border2' }}
      round="8px"
      background="canvas"
      pad={{ horizontal: 'spacing16', vertical: 'spacing16' }}
    >
      <Box direction="row" gap="spacing12" align="start">
        {!forceExpanded && !hideRadio && (
          <Box data-testid={`combine-hsa-section_${sectionType}_card_${account.accountNumber}_radio`}
            height="24px"
            justify="center"
          >
            <RadioButton
              name={`${sectionType}-combine-hsa-${account.accountNumber}`}
              checked={isSelected}
              disabled={isRadioDisabled}
              onChange={handleSelect}
            />
          </Box>
        )}
        {!forceExpanded && (
          <Box
            data-testid={`combine-hsa-section_${sectionType}_card_${account.accountNumber}_expand-toggle`}
            style={{ cursor: 'pointer' }}
            width="24px"
            height="24px"
            align="center"
            justify="center"
            onClick={handleToggle}
          >
            {isExpanded ? <ArrowUpIcon color="iconPrimary" /> : <ArrowDownIcon color="iconPrimary" />}
          </Box>
        )}
        <Box flex direction="column" gap="spacing12">
          <Box data-testid={`combine-hsa-section_${sectionType}_card_${account.accountNumber}_collapsed`} 
            direction="row" 
            gap="spacing16">
            <CardField
              label="Employee"
              value={(
                <TextWithPointer
                  color="textTitle"
                  title={`${account.personFirstName} ${account.personLastName}`}
                  weight="bold"
                  ellipsisMode
                >
                  {`${account.personFirstName} ${account.personLastName}`}
                </TextWithPointer>
              )}
            />
            <CardField
              label="Employer"
              value={(
                <TextWithPointer
                  color="textTitle"
                  title={account.employerName}
                  weight="bold"
                  ellipsisMode
                >
                  {account.employerName}
                </TextWithPointer>
              )}
            />
            <CardField
              label="HSA account number"
              value={(
                <TextWithPointer
                  color="textTitle"
                  title={account.accountNumber}
                  weight="bold"
                  ellipsisMode
                >
                  {account.accountNumber}
                </TextWithPointer>
              )}
            />
          </Box>

          {(isExpanded || forceExpanded) && (
            <Box data-testid={`combine-hsa-section_${sectionType}_card_${account.accountNumber}_expanded`} 
              direction="column" 
              gap="spacing8">
              <Box direction="row" gap="spacing16">
                <CardField
                  label="Partner"
                  value={(
                    <TextWithPointer
                      title={account.partnerName}
                      ellipsisMode
                    >
                      {account.partnerName}
                    </TextWithPointer>
                  )}
                />
                <CardField
                  label="SSN"
                  value={(
                    <Box direction="row" align="center" gap="spacing8">
                      <Inscription lineHeight="20px">{formattedSsn}</Inscription>
                      {account.ssn && (
                        <RawButton
                          data-testid={`combine-hsa-section_${sectionType}_card_${account.accountNumber}_ssn-toggle`}
                          style={{ display: 'flex', alignItems: 'center' }}
                          disabled={!hasSsnAccess}
                          onClick={() => setIsSsnShown((prev) => !prev)}
                        >
                          {isSsnShown ? <EyeIcon color="iconPrimary" /> : <HideIcon color="iconPrimary" />}
                        </RawButton>
                      )}
                    </Box>
                  )}
                />
                <CardField
                  label="HSA account status"
                  value={(
                    <Box direction="row" align="center" gap="spacing6">
                      <StatusDot status={account.accountStatus} />
                      <Inscription>{formatStatus(account.accountStatus)}</Inscription>
                    </Box>
                  )}
                />
              </Box>
              <Box direction="row" gap="spacing16">
                <CardField
                  label="Date of Birth"
                  value={<Inscription>{dayjs(account.birthOn).format(DEFAULT_DATE_FORMAT)}</Inscription>}
                />
                <CardField
                  label="Investments"
                  value={<Inscription>{formatCurrency(account.investorAccountBalance) ?? EMPTY_VALUE}</Inscription>}
                />
                <CardField
                  label="HSA account balance"
                  value={<Inscription>{formatCurrency(account.availableBalance)}</Inscription>}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {hasInvestmentWarning && (
        <Box data-testid={`combine-hsa-section_${sectionType}_card_${account.accountNumber}_warning-investment`}>
          <WarningCard
            topBorderColor="transparent"
            borderColor="warningBorder"
            icon={<WarningIcon />}
            containerProps={{ pad: { vertical: 'spacing12', horizontal: 'spacing16' } }}
            title={<Inscription color="textBody">{INVESTMENT_WARNING_MESSAGE}</Inscription>}
          />
        </Box>
      )}
      {hasZeroBalanceWarning && (
        <Box data-testid={`combine-hsa-section_${sectionType}_card_${account.accountNumber}_warning-zero-balance`}>
          <WarningCard
            topBorderColor="transparent"
            borderColor="warningBorder"
            icon={<WarningIcon />}
            containerProps={{ pad: { vertical: 'spacing12', horizontal: 'spacing16' } }}
            title={<Inscription color="textBody">{ZERO_BALANCE_MESSAGE}</Inscription>}
          />
        </Box>
      )}
      {hasPendingWarning && (
        <Box data-testid={`combine-hsa-section_${sectionType}_card_${account.accountNumber}_warning-pending`}>
          <WarningCard
            topBorderColor="transparent"
            borderColor="warningBorder"
            icon={<WarningIcon />}
            containerProps={{ pad: { vertical: 'spacing12', horizontal: 'spacing16' } }}
            title={<Inscription color="textBody">{PENDING_ACCOUNT_MESSAGE}</Inscription>}
          />
        </Box>
      )}
    </CardWrapper>
  );
};
